import { useCallback, useEffect, useState } from 'react';
import { getEventConfirmedMembers } from '../api/tournamentsApi';

/**
 * Строит объединённый список участников для редактирования турнира:
 *   1. Игроки турнира (в порядке как в турнире), включая удалённых из события — флаг removedFromEvent.
 *   2. Новые участники события, которых ещё нет в турнире — флаг newToTournament.
 *
 * Используется в TournamentPage (DRAFT) и TournamentEditView (RUNNING).
 *
 * @param {string|null} eventPublicId
 * @param {Array}       tournamentPlayers  — tournament.players
 * @param {boolean}     skip              — не загружать (например, турнир не в нужном статусе)
 * @returns {{ members: Array, loading: boolean, refetch: Function }}
 *   refetch() — принудительно перезагрузить список (например, после того как через
 *   AddParticipantPanel в событие добавили нового участника — playersKey при этом
 *   не меняется, т.к. tournament.players ещё не в курсе о новичке, а без refetch
 *   он не появится в списке до следующего реального изменения состава турнира).
 */
const useEventMembers = (eventPublicId, tournamentPlayers, skip = false) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(!skip);
  const [refreshTick, setRefreshTick] = useState(0);

  // Стабильный ключ состава — строка из id игроков и их статусов.
  // Меняется только при реальном изменении состава, не при каждом ре-рендере.
  const playersKey = (tournamentPlayers || [])
    .map(p => `${p.organizationMemberId}:${p.status}`)
    .join(',');

  useEffect(() => {
    if (skip || !eventPublicId) {
      setMembers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    getEventConfirmedMembers(eventPublicId)
      .then(confirmed => {
        const confirmedList = confirmed || [];
        const confirmedIds  = new Set(confirmedList.map(m => m.organizationMemberId));

        // 1. Игроки турнира (в порядке как в турнире), в т.ч. удалённые из события.
        // Сохраняем поля имени/ника/алиаса (...p) для единого отображения.
        const tournamentMembers = (tournamentPlayers || []).map(p => ({
          ...p,
          removedFromEvent: !confirmedIds.has(p.organizationMemberId),
        }));
        const tournamentMemberIds = new Set(tournamentMembers.map(m => m.organizationMemberId));

        // 2. Новые участники события, которых ещё нет в турнире — неактивны по умолчанию
        const newEventMembers = confirmedList
          .filter(m => !tournamentMemberIds.has(m.organizationMemberId))
          .map(m => ({ ...m, newToTournament: true }));

        setMembers([...tournamentMembers, ...newEventMembers]);
      })
      .catch(() => setMembers([]))
      .finally(() => setLoading(false));
  // playersKey — стабильный прокси для tournament.players, не вызывает лишних запросов
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventPublicId, skip, playersKey, refreshTick]);

  const refetch = useCallback(() => setRefreshTick(t => t + 1), []);

  return { members, loading, refetch };
};

export default useEventMembers;
